import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WifiJoinModal } from './WifiJoinModal';
import { stationsApi, type AmenityClickCategory, type StationDetailOut } from '@/api/stations';

interface BowAmenity {
  capabilityKey: string;
  label: string;
  icon: JSX.Element;
  /** Versão maior do mesmo ícone — usada na variante 'destaque', onde o
   * tile tem bem mais espaço que o quadrado compacto (feedback Saulo
   * 2026-07-08: ícone ficava desproporcionalmente pequeno ali). */
  iconLarge: JSX.Element;
  route: (stationId: string) => string;
}

// Bow-owned: continuam vindo de `capabilities`, sem curadoria de admin.
// Adicionar loja/lounge/massagem no futuro é só uma linha aqui.
const BOW_AMENITIES: BowAmenity[] = [
  {
    capabilityKey: 'vacuum',
    label: 'Aspirador',
    icon: <img src="/logo/aspirador-bow.png" alt="" className="h-6 w-6 object-contain" />,
    iconLarge: <img src="/logo/aspirador-bow.png" alt="" className="h-9 w-9 object-contain" />,
    route: (stationId) => `/amenities/${stationId}/vacuum?origin=station`
  }
];

interface Props {
  station: StationDetailOut;
  /** 'compact' (default) = fileira de ícones, usado no resumo da estação e
   * no detalhe de estação de 1 carregador só. 'destaque' = card herói pro
   * primeiro serviço Bow + "no entorno" abaixo — usado no detalhamento por
   * tipo de conector (feedback Saulo 2026-07-07, mapfirst-multi-noscroll.jsx
   * · G · `BowAmenitiesDestaque`). Sem nenhum serviço Bow, cai de volta pro
   * layout compacto — o herói fica vazio sem um serviço pra destacar. */
  variant?: 'compact' | 'destaque';
  /** Tom dos tiles compactos. 'dark' = tela de recarga Imersiva (fundo navy,
   * mapfirst-charging.jsx · `ChargingAmenities`). Só afeta a variante compact. */
  tone?: 'light' | 'dark';
  /** Tela de recarga: o aspirador tem CTA próprio (botão "Aspirar meu carro"),
   * então sai da fileira pra não duplicar o destino. */
  excludeVacuum?: boolean;
}

export function AmenityTiles({
  station,
  variant = 'compact',
  tone = 'light',
  excludeVacuum = false
}: Props) {
  const navigate = useNavigate();
  const [wifiOpen, setWifiOpen] = useState(false);
  const links = station.amenity_links;

  // Métrica de engajamento (feedback Saulo 2026-07-06) — fire-and-forget,
  // nunca bloqueia nem quebra a navegação/abertura do link se falhar.
  function track(category: AmenityClickCategory) {
    stationsApi.trackAmenityClick(station.id, category).catch(() => {});
  }

  const bowTiles = BOW_AMENITIES.filter(
    (a) =>
      station.capabilities?.[a.capabilityKey] === true &&
      !(excludeVacuum && a.capabilityKey === 'vacuum')
  );
  const hasRestaurant = !!links.restaurant;
  const hasCafe = !!links.cafe;
  const hasMarket = !!links.market;
  const hasRestroom = !!links.restroom;
  const hasWifi = !!(links.wifi?.ssid && links.wifi?.password);

  if (
    bowTiles.length === 0 &&
    !hasRestaurant &&
    !hasCafe &&
    !hasMarket &&
    !hasRestroom &&
    !hasWifi
  ) {
    return null;
  }

  if (variant === 'destaque' && bowTiles.length > 0) {
    const entornoAll = [
      hasRestaurant && { key: 'restaurant' as const, label: 'Restaurante', href: links.restaurant!, icon: <RestaurantGlyph /> },
      hasCafe && { key: 'cafe' as const, label: 'Café', href: links.cafe!, icon: <CoffeeGlyph /> },
      hasMarket && { key: 'market' as const, label: 'Mercado', href: links.market!, icon: <MarketGlyph /> },
      hasRestroom && { key: 'restroom' as const, label: 'Banheiro', href: links.restroom!, icon: <RestroomGlyph /> }
    ];
    const entorno = entornoAll.filter(
      (x): x is Exclude<(typeof entornoAll)[number], false> => x !== false
    );

    // Versão discreta (feedback Saulo 2026-07-08, mapfirst-multi-noscroll.jsx
    // · G · `BowAmenitiesDestaque` v2): carregar é o protagonista da tela —
    // os serviços Bow viram tiles claros uniformes (não mais 1 herói navy +
    // resto), com o entorno ao lado separado por um divisor.
    return (
      <div className="flex flex-col gap-2.5">
        <div className="flex items-center gap-2.5">
          <span className="flex-shrink-0 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-bow-royal/45">
            Enquanto carrega
          </span>
          <span className="h-px flex-1 bg-bow-royal/8" />
        </div>
        <div className="flex items-stretch gap-2">
          {bowTiles.map((a) => (
            <button
              key={a.capabilityKey}
              type="button"
              onClick={() => navigate(a.route(station.id))}
              className="flex flex-1 flex-col items-center justify-center gap-1.5 rounded-[13px] bg-bow-cream-2/50 px-1.5 py-3 text-center"
            >
              <span className="flex h-12 w-12 items-center justify-center text-bow-royal">
                {a.iconLarge}
              </span>
              <span className="whitespace-nowrap text-[11px] font-semibold text-bow-royal/75">
                {a.label}
              </span>
            </button>
          ))}
          {(entorno.length > 0 || hasWifi) && (
            <>
              <span className="w-px flex-shrink-0 self-stretch bg-bow-royal/8" />
              <div className="flex flex-col justify-center gap-1.5">
                <div className="flex gap-1.5">
                  {entorno.map((a) => (
                    <a
                      key={a.key}
                      href={a.href}
                      target="_blank"
                      rel="noreferrer"
                      onClick={() => track(a.key)}
                      aria-label={a.label}
                      title={a.label}
                      className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-bow-royal/6 text-bow-royal/70"
                    >
                      {a.icon}
                    </a>
                  ))}
                  {hasWifi && (
                    <button
                      type="button"
                      onClick={() => {
                        track('wifi');
                        setWifiOpen(true);
                      }}
                      aria-label="Wi-Fi"
                      title="Wi-Fi"
                      className="flex h-[30px] w-[30px] items-center justify-center rounded-[9px] bg-bow-royal/6 text-bow-royal/70"
                    >
                      <WifiGlyph />
                    </button>
                  )}
                </div>
                <span className="text-center text-[9.5px] font-semibold text-bow-royal/45">
                  no entorno
                </span>
              </div>
            </>
          )}
        </div>
        {wifiOpen && links.wifi && (
          <WifiJoinModal
            ssid={links.wifi.ssid}
            password={links.wifi.password}
            onClose={() => setWifiOpen(false)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-2.5">
        {bowTiles.map((a) => (
          <Tile
            key={a.capabilityKey}
            tone={tone}
            label={a.label}
            onClick={() => navigate(a.route(station.id))}
          >
            {a.icon}
          </Tile>
        ))}
        {hasRestaurant && (
          <Tile
            as="a"
            tone={tone}
            href={links.restaurant!}
            label="Restaurante"
            onClick={() => track('restaurant')}
          >
            <RestaurantGlyph />
          </Tile>
        )}
        {hasCafe && (
          <Tile as="a" tone={tone} href={links.cafe!} label="Café" onClick={() => track('cafe')}>
            <CoffeeGlyph />
          </Tile>
        )}
        {hasMarket && (
          <Tile as="a" tone={tone} href={links.market!} label="Mercado" onClick={() => track('market')}>
            <MarketGlyph />
          </Tile>
        )}
        {hasRestroom && (
          <Tile as="a" tone={tone} href={links.restroom!} label="Banheiro" onClick={() => track('restroom')}>
            <RestroomGlyph />
          </Tile>
        )}
        {hasWifi && (
          <Tile
            tone={tone}
            label="Wi-Fi"
            onClick={() => {
              track('wifi');
              setWifiOpen(true);
            }}
          >
            <WifiGlyph />
          </Tile>
        )}
      </div>

      {wifiOpen && links.wifi && (
        <WifiJoinModal
          ssid={links.wifi.ssid}
          password={links.wifi.password}
          onClose={() => setWifiOpen(false)}
        />
      )}
    </>
  );
}

interface TileProps {
  label: string;
  children: React.ReactNode;
  onClick?: () => void;
  as?: 'a';
  href?: string;
  tone?: 'light' | 'dark';
}

function Tile({ label, children, onClick, as, href, tone = 'light' }: TileProps) {
  const className =
    tone === 'dark'
      ? 'inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl border border-bow-cream/10 bg-bow-cream/10 text-bow-cream transition hover:bg-bow-cream/15'
      : 'inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-bow-royal/5 text-bow-royal transition hover:bg-bow-royal/10';
  if (as === 'a' && href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        aria-label={label}
        title={label}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} aria-label={label} title={label} className={className}>
      {children}
    </button>
  );
}

function RestaurantGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M6 3v7a2 2 0 0 0 4 0V3M8 10v11" />
      <path d="M16 3c-1.5 0-2.5 1.5-2.5 4s1 4 2.5 4M16 3v18" />
    </svg>
  );
}

function CoffeeGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 8h11v5a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4z" />
      <path d="M16 9h2.5a2 2 0 0 1 0 4H16" />
      <path d="M8 3v2M11 3v2" />
      <path d="M5 20h11" />
    </svg>
  );
}

function MarketGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="9" cy="20" r="1.4" />
      <circle cx="17" cy="20" r="1.4" />
      <path d="M3 4h2l2.2 11.5a1 1 0 0 0 1 .8h8.5a1 1 0 0 0 1-.8L20 8H6" />
    </svg>
  );
}

function RestroomGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M12 3v18" />
      <circle cx="7" cy="5.5" r="1.6" />
      <path d="M7 8.5c-1.4 0-2 1-2 2.3V15h1.2l.3 5h1l.3-5H10v-4.2c0-1.3-.6-2.3-2-2.3z" />
      <circle cx="17" cy="5.5" r="1.6" />
      <path d="M17 8.5c-1.4 0-2.2 1-2.6 2.4L13.5 15H15l.5 5h1l.5-5h.1l.4 5h1l.5-5h1.5l-.9-4.1c-.4-1.4-1.2-2.4-2.6-2.4z" />
    </svg>
  );
}

function WifiGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={22}
      height={22}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4.5 9.5a11 11 0 0 1 15 0" />
      <path d="M7.5 13a7 7 0 0 1 9 0" />
      <path d="M10.5 16.5a3 3 0 0 1 3 0" />
      <circle cx="12" cy="19.5" r="0.6" fill="currentColor" stroke="none" />
    </svg>
  );
}
